import { LitElement, html } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { SDSEventDispatcherMixin } from '../mixins/sds-event-dispatcher-mixin';
import { SDSStepperStep } from '../sds-stepper-step/sds-stepper-step';
import { styles } from './sds-stepper.styles';

export interface SDSStepperProps {
	orientation?: 'horizontal' | 'vertical';
}

@guardedCustomElement('sds-stepper')
export class SDSStepper extends SDSEventDispatcherMixin(LitElement) {
	static override styles = [styles];
	public readonly internals: ElementInternals;

	@property({ type: String, reflect: true }) orientation: SDSStepperProps['orientation'] = 'horizontal';

	@queryAssignedElements({ selector: 'sds-stepper-step', flatten: true })
	private readonly steps!: SDSStepperStep[];

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	private readonly _setActiveStep = (steps: SDSStepperStep[], activeIndex: number): void => {
		steps.forEach((step, index) => {
			step.active = index === activeIndex;
		});
	};

	private readonly _handleChildClick = (event: MouseEvent) => {
		event.preventDefault();
		event.stopImmediatePropagation();

		const step: SDSStepperStep | undefined = event.composedPath().find((n): n is SDSStepperStep => n instanceof SDSStepperStep);

		if (!step) return;

		const index = this.steps.indexOf(step);
		if (index === -1) return;

		this._setActiveStep(this.steps, index);
		this.dispatchSheriffEvent('change', { index });
	};

	public getActiveStep(): number {
		return this.steps.findIndex((step) => step.active);
	}

	public override role = 'tablist';

	override connectedCallback() {
		super.connectedCallback();
		if (!this.hasAttribute('aria-label')) {
			this.ariaLabel = 'Steg';
		}
	}

	public goToStep(index: number) {
		if (index < 0 || index >= this.steps.length) return;

		this._setActiveStep(this.steps, index);
		this.dispatchSheriffEvent('change', { index });
	}

	private _applyStepNumbers() {
		this.steps.forEach((step, i) => {
			step.style.setProperty('--sds-stepper-step-counter-content', `"${i + 1}"`);
		});
	}

	override firstUpdated() {
		const userAgent: string = navigator.userAgent;

		const isSafari: boolean = /safari/i.test(userAgent) && !/chrome/i.test(userAgent) && !/crios/i.test(userAgent) && !/edgios/i.test(userAgent) && !/fxios/i.test(userAgent);

		if (isSafari) {
			const slot = this.shadowRoot!.querySelector('slot') as HTMLSlotElement;
			slot.addEventListener('slotchange', () => this._applyStepNumbers());
			this._applyStepNumbers();
		}
	}

	override render() {
		return html`<slot @_sds-step-clicked="${this._handleChildClick}"></slot>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-stepper': SDSStepper;
	}

	interface SDSStepperEventMap {
		change: CustomEvent<{ index: number }>;
	}
}
